import { getApiBaseUrl, type ApiResponse } from '../../auth/api';
import { authFetch } from '../../auth/authFetch';
import type { BsbValidationResponse, BsbValidationSummary } from './bsbValidationApiTypes';

/**
 * Error raised by the BSB verification client for a genuine failure (auth, bad request, or an override target that
 * does not exist). The read endpoint never throws for "not yet evaluated" — that is a legitimate 200 response;
 * see `BsbValidationResponse` and `isNotEvaluated`.
 */
export class BsbValidationRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'BsbValidationRequestError';
  }
}

/** True when the read endpoint reported that the Pay Run has no current BSB evidence yet. */
export const isNotEvaluated = (response: BsbValidationResponse): boolean =>
  response.state === 'not_evaluated';

/** True when the backend refused the action because the caller is not authorised for it. */
export const isForbidden = (failure: unknown): boolean =>
  failure instanceof BsbValidationRequestError && failure.status === 403;

/** True when the session is no longer authenticated. Distinct from a refusal: the operator must sign in again. */
export const isUnauthenticated = (failure: unknown): boolean =>
  failure instanceof BsbValidationRequestError && failure.status === 401;

const base = (businessRecordId: string, payId: string) =>
  `${getApiBaseUrl()}/api/business-records/${encodeURIComponent(businessRecordId)}/payrun/${encodeURIComponent(payId)}/bsb-validation`;

const errorCode = (status: number): string => {
  if (status === 401) return 'BSB_UNAUTHORIZED';
  if (status === 403) return 'BSB_FORBIDDEN';
  if (status === 404) return 'BSB_FINDING_NOT_FOUND';
  if (status === 400) return 'BSB_INVALID_REQUEST';
  return 'BSB_INTERNAL_ERROR';
};

const parse = async <T>(response: Response): Promise<T> => {
  let body: ApiResponse<T>;
  try {
    body = await response.json() as ApiResponse<T>;
  } catch {
    throw new BsbValidationRequestError(response.status, 'BSB_INTERNAL_ERROR', `Request failed: ${response.status}`);
  }
  if (!response.ok || !body.success || body.result == null) {
    throw new BsbValidationRequestError(
      response.status,
      errorCode(response.status),
      body.message || `Request failed: ${response.status}`,
    );
  }
  return body.result;
};

export const getBsbValidation = async (
  businessRecordId: string,
  payId: string,
  signal?: AbortSignal,
): Promise<BsbValidationResponse> =>
  parse<BsbValidationResponse>(await authFetch(base(businessRecordId, payId), { signal }));

/**
 * Overrides a single finding. Mutating Pay Run calls opt out of the unauthorized retry because an override is an
 * immutable, non-reversible audit record and a silent replay could report failure after a successful write.
 */
export const overrideBsbFinding = async (
  businessRecordId: string,
  payId: string,
  findingId: string,
  reason: string,
): Promise<BsbValidationSummary> =>
  parse<BsbValidationSummary>(await authFetch(
    `${base(businessRecordId, payId)}/findings/${encodeURIComponent(findingId)}/override`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ findingId, reason: reason.trim() }),
    },
    { retryUnauthorized: false },
  ));
